import { id } from 'date-fns/locale'
import { CredentialType } from '@/@types/credential'
import {
  CreateKmsOrganizationType,
  IssueCollectionRequest,
  KmsOrganizationType,
} from '@/@types/kms'
import { VESS_ENDPOINT_TYPE } from '@/server/kms'
import { getEnv } from '@/utils/envUtil'
import { isGoodResponse } from '@/utils/http'
import { getCurrentDomain } from '@/utils/url'

export type User = {
  id: string
  name: string | null
  avatar: string | null
  description: string | null
  did: string | null
  email: string | null
  createdAt: Date
  updatedAt: Date
}

export type GetCollectionResponse = {
  id: string
  organizationId: string
  name: string
  parentId?: string
  parent?: GetCollectionResponse
  credentialType: string
  children?: GetCollectionResponse[]
  createdAt: string
  updatedAt: string
}

export type VSCredential = {
  id: string
  organizationId: string
  credentialTypeId: string | null
  issuerDid: string
  holderDid: string
  plainCredential: string
  createdAt: Date
  updatedAt: Date
  credentialItemId: string
}

type Tag = {
  id: string
  name: string
}

type Tagged = {
  tag: Tag
}
export type VSCredentialItemFromBuckup = {
  id: string
  credentialTypeId: string
  credentialType: CredentialType
  collectionId: string
  organizationId: string | null
  title: string
  description: string | null
  icon: string | null
  image: string | null
  Tagged: Tagged[]
  primaryColor: string | null
  startDate: string | null
  endDate: string | null
  createdAt: string
  updatedAt: string
  expirationDate?: string
  validDuraion?: number
  credentials?: VSCredential[]
  sticker?: Sticker[]
}

export type OBCredentialItemFromBackup = {
  id: string
  credentialTypeId: string
  collectionId: string
  organizationId: string | null
  name: string
  description: string
  criteria: string
  image: string
  bakedImage?: string
  achievementType?: string
  activityStartDate?: string
  activityEndDate?: string
  validFrom?: string
  validUntil?: string
  createdAt: string
  updatedAt: string
  credentials?: VSCredential[]
}

export type Sticker = {
  id: string
  credentialItemId: string
  image: string
}

export interface CredentialWithHolder extends VSCredential {
  holder: User | null
}

export type VSCredentialItemFrombackupWithUser = VSCredentialItemFromBuckup & {
  credentialsWithHolder?: CredentialWithHolder[]
}

export type OBCredentialItemFrombackupWithUser = OBCredentialItemFromBackup & {
  credentialsWithHolder?: CredentialWithHolder[]
}

export type GetCollectionItemResponse = GetCollectionResponse & {
  items: VSCredentialItemFromBuckup[]
  obItems: OBCredentialItemFromBackup[]
}

export const updateOrganization = async (
  id: string,
  memberId: string,
  body: any,
): Promise<Response> => {
  try {
    return await baseVessApi('PUT', '/organizations', id, memberId, body)
  } catch (error) {
    throw error
  }
}

export const getAllCollections = async (
  organizationId?: string,
  memberId?: string,
): Promise<GetCollectionResponse[]> => {
  if (!organizationId) {
    throw new Error('organizationId is undefined')
  }
  try {
    const res = await baseVessApi('GET', '/v2/collection/all', organizationId, memberId)
    const resjson = await res.json()
    console.log({ resjson })
    return resjson.collections as GetCollectionResponse[]
  } catch (error) {
    throw error
  }
}

export const getCollection = async (
  id: string,
  memberId: string,
  collectionId?: string,
): Promise<GetCollectionItemResponse> => {
  if (!collectionId) {
    throw new Error('collectionId is undefined')
  }
  try {
    const res = await baseVessApi('GET', '/v2/collection', id, memberId, undefined, collectionId)
    const resjson = await res.json()
    return resjson as GetCollectionItemResponse
  } catch (error) {
    throw error
  }
}

export const getDefaultCollections = async (
  organizationId?: string,
  memberId?: string,
): Promise<GetCollectionItemResponse> => {
  if (!organizationId) {
    throw new Error('organizationId is undefined')
  }
  try {
    const res = await baseVessApi(
      'GET',
      '/v2/collection/default',
      organizationId,
      memberId,
      undefined,
      organizationId,
    )
    const resjson = await res.json()
    return resjson as GetCollectionItemResponse
  } catch (error) {
    throw error
  }
}

export const createCollection = async (
  id: string,
  memberId: string,
  body: IssueCollectionRequest,
): Promise<Response> => {
  try {
    return await baseVessApi('POST', '/v2/collection', id, memberId, body)
  } catch (error) {
    throw error
  }
}

export const getCredentialItem = async (
  itemId?: string,
  showHolders: boolean = false,
  id?: string,
  memberId?: string,
): Promise<VSCredentialItemFromBuckup> => {
  if (!id) {
    throw new Error('id is undefined')
  }
  try {
    const res = await baseVessApi(
      'GET',
      '/v2/collection/items',
      id,
      memberId,
      undefined,
      itemId,
      showHolders ? 'showHolders=true' : undefined,
    )
    const resjson = await res.json()
    console.log('obCredItem:', resjson as VSCredentialItemFromBuckup)
    return resjson as VSCredentialItemFromBuckup
  } catch (error) {
    throw error
  }
}

export const getOBCredentialItem = async (
  itemId?: string,
  showHolders: boolean = false,
  id?: string,
  memberId?: string,
): Promise<OBCredentialItemFromBackup> => {
  if (!id) {
    throw new Error('id is undefined')
  }
  try {
    const res = await baseVessApi(
      'GET',
      '/v2/collection/ob-items',
      id,
      memberId,
      undefined,
      itemId,
      showHolders ? 'showHolders=true' : undefined,
    )
    const resjson = await res.json()
    console.log('obCredItem:', resjson as OBCredentialItemFromBackup)
    return resjson as OBCredentialItemFromBackup
  } catch (error) {
    throw error
  }
}

export const createCredentialItem = async (
  id: string,
  memberId: string,
  body: any,
): Promise<Response> => {
  try {
    return await baseVessApi('POST', '/v2/creditems/org/items', id, memberId, body)
  } catch (error) {
    throw error
  }
}

export const createOBCredentialItem = async (
  id: string,
  memberId: string,
  body: any,
): Promise<Response> => {
  try {
    return await baseVessApi('POST', '/v2/creditems/org/ob-items', id, memberId, body)
  } catch (error) {
    throw error
  }
}

export const deleteCredItems = async (
  id: string,
  memberId: string,
  credItemId: string,
): Promise<Response> => {
  try {
    return await baseVessApi(
      'POST',
      '/v2/creditems/org/items/delete',
      id,
      memberId,
      undefined,
      credItemId,
    )
  } catch (error) {
    throw error
  }
}

export const getCredentialsByIssuer = async (
  did?: string,
  id?: string,
  memberId?: string,
): Promise<Response> => {
  if (!did) {
    throw new Error('did is undefined')
  }
  try {
    return await baseVessApi('GET', '/v2/credential/issuer', id, memberId, undefined, did)
  } catch (error) {
    throw error
  }
}

export const issueVerifiableCredentials = async (
  body: any,
  id?: string,
  memberId?: string,
): Promise<Response> => {
  try {
    return await baseVessApi('POST', '/v2/credential/issue', id, memberId, body)
  } catch (error) {
    throw error
  }
}

export const updateVerifiableCredentials = async (
  body: any,
  id?: string,
  memberId?: string,
): Promise<Response> => {
  try {
    return await baseVessApi('POST', '/v2/credential/update', id, memberId, body)
  } catch (error) {
    throw error
  }
}

export const getKmsOrganizations = async (): Promise<KmsOrganizationType[]> => {
  try {
    const res = await baseVessApi('GET', '/organizations')
    if (isGoodResponse(res.status)) {
      const json = await res.json()
      return json as KmsOrganizationType[]
    } else {
      console.log('error', res)
    }
    return []
  } catch (error) {
    console.error(error)
    return []
  }
}

export const createKmsOrganizations = async (
  body: CreateKmsOrganizationType,
): Promise<Response> => {
  try {
    const res = await baseVessApi('POST', '/organizations', undefined, undefined, body)
    return res
  } catch (error) {
    throw error
  }
}
export const getKmsSession = async (id: string, memberId?: string): Promise<Response> => {
  try {
    return await baseVessApi('POST', '/session', id, memberId)
  } catch (error) {
    throw error
  }
}

export const addMembers = async (
  id: string,
  memberId: string,
  email: string,
  role?: string,
): Promise<Response> => {
  try {
    return await baseVessApi('POST', '/members', id, memberId, {
      email: email,
      role: role,
    })
  } catch (error) {
    throw error
  }
}

export const getMembers = async (id: string, memberId: string): Promise<Response> => {
  try {
    return await baseVessApi('GET', '/members/all', id, memberId)
  } catch (error) {
    throw error
  }
}

export const getMember = async (id: string): Promise<Response> => {
  try {
    return await baseVessApi('GET', '/members', id)
  } catch (error) {
    throw error
  }
}

export const setRole = async (
  id: string,
  memberId: string,
  targetMemberId: string,
  role: string,
): Promise<Response> => {
  try {
    return await baseVessApi('PUT', '/members/role', id, memberId, { role: role }, targetMemberId)
  } catch (error) {
    throw error
  }
}

export const deleteMember = async (
  id: string,
  memberId: string,
  targetMemberId: string,
): Promise<Response> => {
  try {
    return await baseVessApi('POST', '/members/delete', id, memberId, undefined, targetMemberId)
  } catch (error) {
    throw error
  }
}

const baseVessApi = async (
  method: 'GET' | 'POST' | 'PUT' = 'POST',
  endpoint: VESS_ENDPOINT_TYPE,
  id?: string,
  memberId?: string,
  body?: any,
  slug?: string,
  query?: string,
): Promise<Response> => {
  try {
    const vessOrgId = encodeURIComponent(id || '')
    const vessMemberId = encodeURIComponent(memberId || '')
    let url = `${
      getCurrentDomain() || getEnv('AUTH0_BASE_URL')
    }/api/vessApi?endpoint=${endpoint}&vessOrgId=${vessOrgId}&vessMemberId=${vessMemberId}&slug=${
      slug || ''
    }`
    if (query) {
      url = url + `&q=${encodeURIComponent(query)}`
    }
    if (method === 'GET') {
      return await fetch(url)
    } else if (method === 'PUT') {
      return await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...body }),
      })
    } else {
      return await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...body }),
      })
    }
  } catch (error) {
    console.error(error)
    throw error
  }
}
